import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "../lib/ui";
import { withApp } from "../lib/story";

const STATE_TONE = {
  done: "green",
  current: "cyan",
  ready: "amber",
  pending: "slate",
  blocked: "rose",
};

function TenantTree({ tenantScope, programSelection }) {
  if (!tenantScope) return null;
  return (
    <div className="g1-tenant-tree" data-testid="g1-tenant-tree">
      <div className="g1-hierarchy-node">
        <strong>{tenantScope.label}</strong>
        <span className="muted small-note">{tenantScope.detail}</span>
      </div>
      {(tenantScope.children || []).map((child) => (
        <div key={child.id} className="g1-hierarchy-node branch" data-testid={`tenant-child-${child.id}`}>
          <span className="tree-prefix">├──</span>
          <strong>{child.label}</strong>
          <span className="muted small-note">{child.detail}</span>
          {child.id === "portfolio" && (child.children || []).map((app) => (
            <div key={app.id} className="g1-hierarchy-node branch portfolio-app" data-testid={`portfolio-app-${app.id}`}>
              <span className="tree-prefix">│   ├──</span>
              <strong>{app.label}</strong>
              <span className="muted small-note">{app.detail}</span>
              {app.is_current && <Badge tone="cyan">current</Badge>}
            </div>
          ))}
          {child.values && (
            <span className="muted small-note">{(child.values || []).join(" · ")}</span>
          )}
        </div>
      ))}
      {programSelection && (
        <div className="g1-program-select" data-testid="program-selection">
          <p className="kicker">MODERNIZATION PROGRAM</p>
          <div className="g1-hierarchy-node">
            <strong>{programSelection.label}</strong>
            <span className="muted small-note">{programSelection.detail}</span>
            {programSelection.selects && (
              <span className="muted small-note">selects / candidates {programSelection.selects}</span>
            )}
          </div>
          <p className="muted small-note">{programSelection.rule}</p>
        </div>
      )}
    </div>
  );
}

/** Tenant hierarchy + linear G1 workflow from backend model. */
export default function G1FlowDiagram({ g1, appId }) {
  if (!g1) return null;
  const workflow = g1.g1_workflow || g1.discovery_flow || {};
  const steps = g1.pre_g1_steps || workflow.steps || [];
  const fork = g1.decision_fork || {};
  const progress = g1.workflow_progress || workflow.progress;

  return (
    <section className="feature-panel analytics-panel" data-testid="g1-flow-diagram">
      <TenantTree tenantScope={g1.hierarchy?.tenant_scope} programSelection={g1.hierarchy?.program_selection} />

      <p className="kicker" style={{ marginTop: "1rem" }}>G1 WORKFLOW</p>
      <p className="muted small-note">{g1.sequence}</p>
      {progress && (
        <p className="muted small-note" data-testid="g1-workflow-progress">
          {progress.done}/{progress.total} steps complete · current: {g1.current_step || "—"}
        </p>
      )}

      <div className="g1-workflow-track" data-testid="g1-workflow-track">
        {steps.map((step, index) => (
          <div key={step.id} className="g1-workflow-row" data-testid={`g1-step-${step.id}`}>
            <Link
              to={withApp(step.route, appId)}
              className={`lifecycle-phase-link state-${step.state || "pending"} g1-workflow-step`}
            >
              <small>{String(index + 1).padStart(2, "0")}</small>
              <strong>{step.label}</strong>
              <span>{step.detail}</span>
              <Badge tone={STATE_TONE[step.state] || "slate"}>{(step.state || "pending").replaceAll("_", " ")}</Badge>
              <ChevronRight size={16} />
            </Link>
            {index < steps.length - 1 && (
              <span className="g1-workflow-arrow" aria-hidden="true"><ChevronDown size={14} /></span>
            )}
          </div>
        ))}
      </div>

      {fork.proceed && (
        <div className="g1-decision-fork" data-testid="g1-decision-fork">
          <div className="metric-grid">
            <div className="metric">
              <div><small>{fork.proceed.label}</small></div>
              <p className="muted small-note">{fork.proceed.note}</p>
            </div>
            <div className="metric">
              <div><small>{fork.do_not_proceed?.label}</small></div>
              <p className="muted small-note">{fork.do_not_proceed?.note}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
